import React, {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useRechargeStore} from '../recharge.store';
import './recharge-tab.css';
import {getActivityPayList} from './recharge-tab.service';
import {RechargeWebActivityPayTabs} from '@fe-progressive/package-progressive/recharge-web';
import {normalizeProgressivePackageLocale} from '@fe-progressive/package-progressive/locale';
import {ProgressiveCircularLoading} from '@fe-progressive/package-progressive/progressive-loading';

/** 周卡活动 tab 的后台约定 id，Crypto 列表中不展示 */
const WEEKLY_CARD_ACTIVITY_TAB_ID = -2;

const CORNER_RIBBON_BG = require('@/assets/imgs/recharge/tag.webp') as string;

export interface RechargeTabProps {
  onTabChange?: (tabId: number) => void;
  loading?: boolean;
  useType?: number;
  autoLoad?: boolean;
  /** 覆盖 `useTranslation().i18n.language`，供包内默认文案 */
  locale?: string;
  /** 选中角标 SVG 对号描边；默认白 */
  selectionBadgeCheckStroke?: string;
}

const RechargeTab: React.FC<RechargeTabProps> = ({
  onTabChange,
  loading: externalLoading,
  useType,
  autoLoad = true,
  locale: localeProp,
  selectionBadgeCheckStroke,
}) => {
  const {i18n} = useTranslation();
  const activityPayTabsLocale = useMemo(
    () => normalizeProgressivePackageLocale(localeProp ?? i18n.language),
    [localeProp, i18n.language],
  );
  const {
    tabs,
    activeTabId,
    loading: storeLoading,
    setActiveTabId,
    setTabs,
  } = useRechargeStore();

  const loading =
    externalLoading !== undefined ? externalLoading : storeLoading;

  const handleTabPress = useCallback(
    (tabId: number) => {
      setActiveTabId(tabId);
      const {updateTabActiveStatus} = useRechargeStore.getState();
      updateTabActiveStatus(tabId, true);
      onTabChange?.(tabId);
    },
    [setActiveTabId, onTabChange],
  );

  useEffect(() => {
    if (autoLoad) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, useType]);

  const init = async () => {
    const res = await getActivityPayList(useType);
    const list =
      useType === 2
        ? res.filter(tab => tab.id !== WEEKLY_CARD_ACTIVITY_TAB_ID)
        : res;
    setTabs(list);
  };

  if (tabs.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <ProgressiveCircularLoading size={24} />
      </div>
    );
  }

  return (
    <RechargeWebActivityPayTabs
      tabs={tabs}
      activeTabId={activeTabId ?? tabs[0]!.id}
      onSelectTab={handleTabPress}
      locale={activityPayTabsLocale}
      cornerRibbonBackgroundUrl={CORNER_RIBBON_BG}
      selectionBadgeCheckStroke={selectionBadgeCheckStroke}
    />
  );
};

export default RechargeTab;
